import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import * as Sentry from '@sentry/angular';
import {
	CrewStatus,
	DisplayedColumn,
	DisplayedColumns,
	Flight,
	FlightCrewInput,
	FlightStatus,
	Tag,
	TenantCurrency,
	UpcomingFlightScope,
	UserRole,
} from 'src/app/core/graphql/generated/gen-types';
import { FlightService } from 'src/app/core/services/flight/flight.service';
import { SnackBar } from 'src/app/core/utility/snackBar';
import { ResponsiveService } from 'src/app/core/services/admin/responsive.service';
import { Router } from '@angular/router';
import { EventService } from 'src/app/core/services/event/event.service';
import { DialogEventComponent } from '../../../features/dialog/dialog-event/dialog-event.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';
import { TagService } from 'src/app/core/services/tag/tag.service';
import { environment } from 'src/environments/environment';
import { DialogFlightPilotCrewComponent } from 'src/app/features/dialog/dialog-flight-pilot-crew/dialog-flight-pilot-crew.component';
import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import { FlightBulkService } from 'src/app/core/services/flight/flight-bulk.service';
import {
	UpdateDisplayedColumnsComponent,
	validDisplayedColumns,
} from '../../displayedColumns/update-displayed-columns/update-displayed-columns.component';

dayjs.extend(weekday);

@Component({
	selector: 'app-flight-table-is',
	templateUrl: './flight-table-is.component.html',
	styleUrls: ['./flight-table-is.component.scss'],
})
export class FlightTableIsComponent implements OnInit {
	// Table
	@Input() type: string;
	@Input() filterValue: string;
	@Input() filterRange: { startDate: any; endDate: any };
	@Input() freeSpots: number;
	@Input() viewState: any;
	@Input() userRole: UserRole;
	@Input() selectedFilterTags: Tag[] = [];
	// 235: type 'upcoming' only — the flight list's status: Upcoming (CURRENT, oldest first) or Not closed
	// (older) (OLDER, most recent first), split by the backend 7 days back. null = all upcoming flights.
	@Input() scope: UpcomingFlightScope = null;
	@Output() addBookingToFlight = new EventEmitter<Flight>();
	// 235: number of loaded rows after each page (lets the flight list show "found in …" when empty)
	@Output() loaded = new EventEmitter<number>();
	// 235 v3: whether every row of the list is loaded (the filter row shows "N+" otherwise)
	@Output() complete = new EventEmitter<boolean>();

	tableHeight: number = 0;

	tenant: string;

	// component guards
	guards = environment.componentGuards.flightList;
	flightCounters; //Amount of total flights
	flights: Flight[] = [];
	datasourceFlights = null;
	displayedColumnsParent: DisplayedColumn[] = [];
	displayedColumnsId: string = 'flightsUpcoming';
	operatorDisplayedColumns: DisplayedColumns;
	validCustom: boolean;
	displayedColumns: string[] = [];

	cursorUpcomingDate: string;

	cursorHistoricalDate: string;

	flightMoreSub: Subscription;

	isLoadingFlights = true;
	allUpcomingFlightsLoaded = false;
	allHistoricalFlightsLoaded = false;

	//Table settings
	flightsLimit = 50;

	isSmall: boolean;
	isXSmall: boolean;
	isMobile: boolean;
	screensize: number;
	amountOfRequests = 1;

	loadingIndexHistorical = 0;
	loadingIndexUpcoming = 0;

	loadTrigger = 80; // percentage of scrollProgress to trigger a new load

	loadingViewState = true;
	// idOfflightToDelete = '';
	flightSubs: Subscription[] = [];

	flightUpdate: boolean = false;

	statusColumnWidth: number = 65;

	meetingTimeOffset: number = 0;
	checkinEnabled = false;

	currency: TenantCurrency;

	// Sentry performance instrumentation thresholds (BUG-022).
	private readonly slowFlightLoadThresholdMs = 200;

	@ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef<HTMLElement>;
	private readonly onScroll = (e: Event) => this.scrollHandler(e);

	// getCellStyles output only depends on the breakpoint and the three widths, but it
	// is called ~24× per row on every change-detection cycle. Returning a cached object
	// (identical identity per key) makes each ngStyle differ see "no change" instead of
	// diffing a fresh object — at a few hundred rows this removes thousands of
	// allocations per CD cycle (BUG-022 follow-up).
	private cellStylesCache = new Map<string, { [key: string]: string }>();

	constructor(
		private flightService: FlightService,
		private tenantSettingService: TenantSettingService,
		private translate: TranslateService,
		private eventService: EventService,
		private snackBar: SnackBar,
		private responsiveService: ResponsiveService,
		private tagService: TagService,
		public dialog: MatDialog,
		private router: Router,
		private zone: NgZone,
		public bulk: FlightBulkService,
	) {}

	ngOnInit(): void {
		dayjs.locale(this.translate.getDefaultLang());
		this.adjustTableHeight(window.innerHeight);

		// The scroll listener fires every frame while the operator scrolls; bound in the
		// template it would run change detection over every loaded row per event. Attach
		// it outside the Angular zone and only re-enter when the load trigger fires
		// (BUG-022 follow-up).
		this.zone.runOutsideAngular(() => {
			this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll, { passive: true });
		});

		// responsiveService Subscriptions
		this.responsiveService.SmallChanged.subscribe((isSmall) => {
			this.isSmall = isSmall;
		});
		this.responsiveService.XSmallChanged.subscribe((isXSmall) => {
			this.isXSmall = isXSmall;
		});
		this.responsiveService.viewMobileChanged.subscribe((mobileView) => {
			this.isMobile = mobileView;
			this.displayedColumns = this.getDisplayedColumns();
		});

		// 221 bulk actions: the bulk bar selects "all matching" with the exact arguments of this
		// list's first page, and asks for a reload after an action or undo
		if (this.type === 'upcoming') {
			this.bulk.listArgsProvider = () => ({
				filter: this.selectedFilterTags.map((tag) => this.tagService.addPrefix(tag)).concat(this.filterValue),
				filterRange: this.filterRange ?? null,
				freeSpots: this.freeSpots ?? 0,
				scope: this.scope ?? null,
			});
			this.flightSubs.push(
				this.bulk.reload$.subscribe(() => {
					this.resetTable();
					this.getData();
				}),
				// the checkbox column only exists while bulk mode is on
				this.bulk.mode$.subscribe(() => (this.displayedColumns = this.getDisplayedColumns())),
			);
		}

		this.tenantSettingService.getTenantSetting().valueChanges.subscribe({
			next: (result) => {
				this.meetingTimeOffset = result.data.tenantSetting.operatorSettings.flights.meetingTimeOffset;
				this.operatorDisplayedColumns = result.data.tenantSetting.operatorSettings.displayedColumns;
				this.displayedColumns = this.getDisplayedColumns();
				this.tenant = result.data.tenantSetting.name;
				this.checkinEnabled = result.data.tenantSetting.operatorSettings?.myflight?.checkinMode === 'ONLINE';
				this.currency = result.data.tenantSetting?.operatorSettings?.currency;
			},
		});
	}

	adjustTableHeight(innerHeight) {
		this.tableHeight = innerHeight - 360;
	}

	ngOnChanges(changes: SimpleChanges) {
		// 235 v3: search, tags, period and free spots can change together (e.g. "Clear all") → reload
		// the list once per change detection instead of once per changed input
		let reload = false;
		if (changes.filterValue && changes.filterValue.currentValue !== null) {
			this.filterValue = changes.filterValue.currentValue;
			reload = true;
		}
		if (changes.freeSpots && changes.freeSpots.currentValue !== null) {
			this.freeSpots = changes.freeSpots.currentValue;
			reload = true;
		}
		if (changes.selectedFilterTags && changes.selectedFilterTags.currentValue) {
			reload = true;
		}
		if (changes.filterRange) {
			if (changes.filterRange.currentValue === '') this.filterRange = null;
			reload = true;
		}

		if (changes.scope && !changes.scope.firstChange) {
			reload = true;
		}

		if (changes.viewState && changes.viewState.currentValue) {
			//load table
			this.loadingViewState = false;
			// 235 review U6: a later viewState (the list saved its filter → GetUser refetch) does not
			// reload; it only appended a duplicate page load
			if (!changes.viewState.previousValue && !reload) {
				this.getData();
			}
		}

		if (changes.userRole && changes.userRole.currentValue) {
			this.displayedColumns = this.getDisplayedColumns();
		}
		if (reload) {
			this.resetTable();
			this.getData();
		}
	}


	ngOnDestroy(): void {
		this.scrollContainer.nativeElement.removeEventListener('scroll', this.onScroll);
		if (this.type === 'upcoming') this.bulk.listArgsProvider = null;
		if (this.flightSubs.length > 0) {
			this.flightSubs.forEach((sub) => sub.unsubscribe());
		}
	}

	private getAmountOfRequests(): void {
		const heightOfTable = window.innerHeight - 270;
		const heightOfRows = 49 * this.flightsLimit; //49px is the fixed height of each column in the table
		this.amountOfRequests = Math.ceil(heightOfTable / heightOfRows);
	}

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.adjustTableHeight(event.target.innerHeight);
		if (this.loadingViewState) return;

		const heightOfTable = event.target.innerHeight - 220;
		const heightOfRows = 49 * this.flights.length;
		if (heightOfTable >= heightOfRows) {
			this.getData();
		}
	}

	private getData(): void {
		if (this.loadingViewState) return;
		this.getAmountOfRequests();
		if ((this.type === 'upcoming' || this.type === 'booking') && !this.allUpcomingFlightsLoaded) {
			this.getUpcomingFlightList();
		} else if (this.type === 'historical' && !this.allHistoricalFlightsLoaded) {
			this.getHistoricalFlightList();
		}
	}

	private emitComplete(): void {
		this.complete.emit(this.type === 'historical' ? this.allHistoricalFlightsLoaded : this.allUpcomingFlightsLoaded);
	}

	getUpcomingFlightList() {
		this.isLoadingFlights = true;
		this.loadingIndexUpcoming += 1;
		const index = this.loadingIndexUpcoming;
		const filter: string[] = this.selectedFilterTags.map((tag) => this.tagService.addPrefix(tag)).concat(this.filterValue);
		const cursor = this.cursorUpcomingDate;
		// 235: the status split only applies to the flight list (not the booking's flight picker)
		const scope = this.type === 'upcoming' ? (this.scope ?? null) : null;
		const flightSub = this.flightService
			.getFlightsList(this.flightsLimit, cursor, filter, this.filterRange, scope === UpcomingFlightScope.Older ? 0 : this.freeSpots, scope)
			.valueChanges.subscribe({
				next: (result) => {
					if (index === this.loadingIndexUpcoming || this.flightUpdate) {
						this.flightUpdate = false;
						this.appendAndRebuildDatasource('upcoming', result.data.getFlightsList, true);
						this.cursorUpcomingDate = [...this.flights].pop()?.date;

						if (result.data.getFlightsList.length < this.flightsLimit) {
							this.allUpcomingFlightsLoaded = true;
						}
						this.isLoadingFlights = false;
						this.emitComplete();
						// Request more flights if table is not filled enough (this can happen when user has a bigger screen).
						if (this.amountOfRequests > 1) {
							this.getUpcomingFlightList();
							this.amountOfRequests -= 1;
						}
					}
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
		this.flightSubs.push(flightSub);
	}

	getHistoricalFlightList() {
		this.isLoadingFlights = true;
		this.loadingIndexHistorical += 1;
		const index = this.loadingIndexHistorical;
		const filter: string[] = this.selectedFilterTags.map((tag) => this.tagService.addPrefix(tag)).concat(this.filterValue);
		const flightSub = this.flightService
			.getHistoricalFlightsList(this.flightsLimit, this.cursorHistoricalDate, filter, this.filterRange)
			.valueChanges.subscribe({
				next: (result) => {
					if (index === this.loadingIndexHistorical || this.flightUpdate) {
						this.flightUpdate = false;
						this.appendAndRebuildDatasource('historical', result.data.getHistoricalFlightsList, false);
						this.cursorHistoricalDate = [...this.flights].pop()?.date;
						if (result.data.getHistoricalFlightsList.length < this.flightsLimit) {
							this.allHistoricalFlightsLoaded = true;
						}
						this.isLoadingFlights = false;
						this.emitComplete();
						if (this.amountOfRequests > 1) {
							this.getHistoricalFlightList();
							this.amountOfRequests -= 1;
						}
					}
				},
				error: (err) => {
					this.snackBar.openSnackBarError(err.message);
				},
			});
		this.flightSubs.push(flightSub);
	}

	private sortFlightList(isUpcoming: boolean) {
		// 235: Not closed (older) lists upcoming-status flights most recent first, like the finished list
		const ascending = isUpcoming && !(this.type === 'upcoming' && this.scope === UpcomingFlightScope.Older);
		if (isUpcoming) {
			this.flights = this.flights.filter((flight) => flight.status !== FlightStatus.Can && flight.status !== FlightStatus.Fin);
		}

		// keep most recent version (reverse) of a flight in case of duplicates (filter)
		this.flights = this.flights.reverse().filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

		// Sort by flight date (ascending for upcoming, descending for historical) with flight
		// id as a stable tie-break for same-date flights. Replaces the previous two full sorts
		// (id then date) and the per-comparison `new Date()` parsing (BUG-022): timestamps are
		// parsed once up front. A stable date+id comparator reproduces the original ordering,
		// which relied on Array.sort stability to keep the id order within equal dates.
		const timeById = new Map<string, number>();
		for (const f of this.flights) {
			timeById.set(f.id, new Date(f.date).getTime());
		}
		this.flights.sort((a, b) => {
			const diff = timeById.get(a.id) - timeById.get(b.id);
			if (diff !== 0) {
				return ascending ? diff : -diff;
			}
			return a.id.localeCompare(b.id);
		});
	}

	private enrichFlightList(flights) {
		let maxIcons = 0;
		// Weekend detection is locale-aware (dayjs `.weekday()` numbering depends on the
		// locale's first day of week, set at construction). Compute the Saturday/Sunday
		// indices once per call under the current locale instead of twice per flight (BUG-022).
		const saturdayIndex = dayjs('2020-10-31').weekday();
		const sundayIndex = dayjs('2020-11-01').weekday();
		// Pre-compute duplicate-balloon and duplicate-pilot lookups in a single pass (O(n))
		// instead of per-flight scans (O(n²)). The flight list grows on every infinite-scroll
		// trigger, so the previous quadratic cost dominated render time once a few hundred
		// flights had been loaded — see BUG-022.
		const balloonBucketCount = new Map<string, number>();
		const pilotBucketCount = new Map<string, number>();
		const flightDateKey = new Map<string, string>(); // flight.id -> local calendar-day key
		for (const f of flights) {
			let count = 1; // status icon is always visible
			if (f.period === 'MOR') count++;
			if (f.passengersData?.childCount > 0) count++;
			if (f.passengersData?.transportCount > 0) count++;
			if (f.passengersData?.disabilityCount > 0) count++;
			// NOTE: the NOTAM indicator lives in the LOCATION cell, not the status
			// stack — deliberately not counted here.
			if (count > maxIcons) maxIcons = count;

			// Cheap per-day grouping key: local Y-M-D from a single Date parse. Only used
			// internally for the balloon/pilot duplicate buckets, so the exact string format
			// is irrelevant as long as it is consistent — avoids a costly dayjs().format() per flight.
			const d = new Date(f.date);
			const dateKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
			flightDateKey.set(f.id, dateKey);

			const balloonKey = `${dateKey}|${f.period}|${f.balloon?.name}`;
			balloonBucketCount.set(balloonKey, (balloonBucketCount.get(balloonKey) ?? 0) + 1);
			if (f.pilot) {
				const pilotKey = `${dateKey}|${f.period}|${f.pilot?.crew?.name}`;
				pilotBucketCount.set(pilotKey, (pilotBucketCount.get(pilotKey) ?? 0) + 1);
			}
		}
		this.statusColumnWidth = Math.max(65, maxIcons * 30 + 16);
		return flights.map((flight) => {
			// add weekend property (reuses the per-call Saturday/Sunday indices computed above)
			const flightWeekday = dayjs(flight.date).weekday();
			const weekend = flightWeekday === saturdayIndex || flightWeekday === sundayIndex;

			const dateKey = flightDateKey.get(flight.id);

			// add duplicate balloon property — bucket count > 1 means another flight in the
			// same (date, period, balloon name) group exists. Preserves original semantics
			// including grouping by undefined balloon name.
			const balloonKey = `${dateKey}|${flight.period}|${flight.balloon?.name}`;
			const duplicateBalloon = (balloonBucketCount.get(balloonKey) ?? 0) > 1;

			// add duplicate pilot property
			const pilotKey = `${dateKey}|${flight.period}|${flight.pilot?.crew?.name}`;
			const duplicatePilot = !!flight.pilot && (pilotBucketCount.get(pilotKey) ?? 0) > 1;

			// NOTAM indicator (095, FR-005): precomputed on the row like duplicateBalloon —
			// the denormalized flight.notam field is the only data source (no extra reads),
			// and the severity class is a plain string so no fresh ngStyle objects are created
			const notamSeverity = flight.notam?.severity ?? null;
			const notamCount = flight.notam?.count ?? 0;
			const notamClass = notamSeverity === 'RED' ? 'notam-icon-red' : 'notam-icon-amber';

			// add filter strings
			const findFilterBalloonName = this.findFilterString(flight?.balloon?.name);
			const findFilterLocationName = this.findFilterString(flight.location?.name);
			const findFilterPilotName = this.findFilterString(flight?.pilot?.crew?.name);
			const findFilterBookings = flight.bookingsData?.list.map((booking) => {
				return { found: this.findFilter(booking), contactName: booking.contactName };
			});
			return {
				...flight,
				weekend,
				duplicateBalloon,
				duplicatePilot,
				notamSeverity,
				notamCount,
				notamClass,
				findFilterBalloonName,
				findFilterLocationName,
				findFilterPilotName,
				findFilterBookings,
			};
		});
	}

	toggleVisibility(flight): void {
		this.flightUpdate = true;
		this.flightService.toggleVisibility(flight.id).subscribe(
			() => {},
			(err) => this.snackBar.openSnackBarError(err.message),
		);
	}

	findFilter(booking) {
		let found = false;
		if (this.filterValue) {
			booking.passengers.forEach((pax) => {
				found = found || pax.name.toLowerCase().includes(this.filterValue.toLowerCase());
			});
			found = found || booking.contactName.toLowerCase().includes(this.filterValue.toLowerCase());
		}
		return found;
	}

	findFilterString(displayString) {
		let found = false;
		if (this.filterValue && displayString) {
			found = displayString.toLowerCase().includes(this.filterValue.toLowerCase());
		}
		return found;
	}

	flightClicked(row): void {
		this.navigateToFlight(row.id);
	}

	updateDisplayedColumns() {
		this.dialog.open(UpdateDisplayedColumnsComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			data: {
				displayedColumnsId: this.displayedColumnsId,
			},
		});
	}

	getDisplayedColumns() {
		if (!this.operatorDisplayedColumns || !this.displayedColumnsId) return;
		if (this.type === 'upcoming') this.displayedColumnsId = 'flightsUpcoming';
		if (this.type === 'booking') {
			this.displayedColumnsId = 'flightsBooking';
			this.tableHeight = 600;
		}
		if (this.type === 'historical') this.displayedColumnsId = 'flightsHistory';

		this.validCustom = validDisplayedColumns(
			this.operatorDisplayedColumns[this.displayedColumnsId],
			this.operatorDisplayedColumns[`${this.displayedColumnsId}Default`],
		);
		let displayedColumns = this.validCustom
			? this.operatorDisplayedColumns[this.displayedColumnsId]
			: this.operatorDisplayedColumns[`${this.displayedColumnsId}Default`];

		// apply guards on columns
		if (!this.guards.bookingsColumn.includes(this.userRole)) {
			displayedColumns = displayedColumns.filter((col) => col.name != 'bookings');
		}

		displayedColumns = displayedColumns.filter((col) => col.active);
		if (this.isMobile) displayedColumns = displayedColumns.filter((col) => col.mobile);

		const names = displayedColumns.map((col) => col.name);
		return this.bulkEnabled() ? ['select', ...names] : names;
	}

	// 221: selection column on the upcoming list while bulk mode is on (desktop, Owner + Operator)
	// own width + padding: the first-column padding of mat-table would otherwise clip the checkbox
	readonly selectCellStyle = { 'min-width': '56px', 'max-width': '56px', padding: '0 0 0 8px', overflow: 'visible' };

	bulkEnabled(): boolean {
		return this.type === 'upcoming' && !this.isMobile && this.bulk.active && this.bulk.canBulk(this.userRole);
	}

	toggleAllBulk(event: MatCheckboxChange): void {
		this.bulk.toggleAll();
		// the checked state follows the selection (select-all is async and may be refused above the limit)
		event.source.checked = this.bulk.count > 0;
	}

	toggleBulkSelection(flight, event: MatCheckboxChange): void {
		if (!this.bulk.toggle(flight)) {
			// over the limit: keep the box unticked (the bulk bar shows why)
			event.source.checked = false;
		}
	}

	navigateToFlight(id?): void {
		let url = '/flight';
		if (id) {
			url = url + '/' + id;
		}
		this.router.navigate([url]);
	}

	// NOTAM indicator click (095): deep-link to the flight detail with the weather panel
	// (panel index 3) open at the NOTAM section
	navigateToFlightNotams(flight): void {
		this.router.navigate(['/flight/' + flight.id], { queryParams: { panel: 3 } });
	}

	// only Historical Table
	deleteFlight(flight): void {
		// this.idOfflightToDelete = flight.id;
		this.flightService.delete(flight).subscribe(
			() => {
				// remove from table immediately
				this.flights.splice(
					this.flights.findIndex((f) => f.id === flight.id),
					1,
				);
				this.datasourceFlights = new MatTableDataSource(this.flights);
			},
			(err) => this.snackBar.openSnackBarError(err.message),
		);
	}

	removeEvent(id, date): void {
		this.flightUpdate = true;
		this.eventService.delete(id).subscribe(
			() => {
				this.flightService.getFlightsListFromDate(date).valueChanges.subscribe();
			},
			(err) => this.snackBar.openSnackBarError(err.message),
		);
	}

	addEvent(): void {
		this.flightUpdate = true;
		const dialogRef = this.dialog.open(DialogEventComponent, {
			data: true,
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result.date) {
				this.flightService.getFlightsListFromDate(result.date).valueChanges.subscribe();
			}
		});
	}

	// Runs outside the Angular zone (see ngOnInit) — only re-enter when a load is
	// actually triggered, so plain scrolling no longer runs change detection.
	scrollHandler(e): void {
		const scrollProgress = Math.floor(((e.srcElement.scrollTop + e.srcElement.clientHeight) / e.srcElement.scrollHeight) * 100);
		if (scrollProgress > this.loadTrigger) {
			Sentry.addBreadcrumb({
				category: 'flight-list.scroll',
				message: 'Infinite scroll triggered load',
				level: 'info',
				data: {
					scrollProgress,
					currentFlightCount: this.flights.length,
					type: this.type,
					scope: this.scope,
					allUpcomingFlightsLoaded: this.allUpcomingFlightsLoaded,
					allHistoricalFlightsLoaded: this.allHistoricalFlightsLoaded,
				},
			});
			this.zone.run(() => this.getData());
		}
	}

	/**
	 * BUG-022: Append newly fetched flights, sort/enrich, and rebuild the MatTableDataSource
	 * while measuring each phase. Replaces the four duplicated inline blocks in
	 * getUpcomingFlightList / getHistoricalFlightList.
	 *
	 * Reports timings via Sentry breadcrumbs and, when total client-side processing exceeds
	 * `slowFlightLoadThresholdMs`, captures a warning message with full context so we can
	 * find evidence of the infinite-scroll slowdown in production.
	 */
	// Stable row identity for MatTable. Because appendAndRebuildDatasource() assigns a new
	// MatTableDataSource on every infinite-scroll load, without a trackBy the table would
	// tear down and re-create every rendered row each time — the dominant render cost seen
	// in Sentry traces (hundreds of long-animation-frames). Keying by flight id lets Material
	// reuse existing row DOM and only render the newly appended flights.
	trackByFlightId = (_index: number, row: Flight): string => row?.id;

	private appendAndRebuildDatasource(label: string, fetchedFlights: Flight[], isUpcoming: boolean): void {
		// Wrap the whole client-side processing in a Sentry span so — when performance
		// tracing is sampled — the per-phase timings (sort / enrich / datasource) become
		// searchable span attributes in the trace waterfall, rather than relying solely on
		// the threshold-based captureMessage below. See BUG-022.
		Sentry.startSpan(
			{ name: 'flight-list.appendAndRebuild', op: 'ui.process', attributes: { label, type: this.type } },
			(span) => {
				const fetchedCount = fetchedFlights.length;
				const flightCountBefore = this.flights.length;

				this.flights.push(...fetchedFlights);

				const sortStart = performance.now();
				this.sortFlightList(isUpcoming);
				const sortMs = performance.now() - sortStart;

				const enrichStart = performance.now();
				const enriched = this.enrichFlightList(this.flights);
				const enrichMs = performance.now() - enrichStart;

				const datasourceStart = performance.now();
				this.datasourceFlights = new MatTableDataSource(enriched);
				this.loaded.emit(this.flights.length);
				const datasourceMs = performance.now() - datasourceStart;

				const totalProcessingMs = sortMs + enrichMs + datasourceMs;
				const flightCountAfter = this.flights.length;

				const data = {
					label,
					type: this.type,
					fetchedCount,
					flightCountBefore,
					flightCountAfter,
					sortMs: Math.round(sortMs),
					enrichMs: Math.round(enrichMs),
					datasourceMs: Math.round(datasourceMs),
					totalProcessingMs: Math.round(totalProcessingMs),
					scope: this.scope,
				};

				span.setAttributes(data);

				const isSlow = totalProcessingMs > this.slowFlightLoadThresholdMs;

				Sentry.addBreadcrumb({
					category: 'flight-list.process',
					message: `Flight list processed (${label})`,
					level: isSlow ? 'warning' : 'info',
					data,
				});

				if (isSlow) {
					Sentry.captureMessage(
						`Slow flight list render: ${Math.round(totalProcessingMs)}ms processing for ${flightCountAfter} flights`,
						{
							level: 'warning',
							tags: {
								feature: 'flight-list-infinite-scroll',
								event: 'flight-list-process-slow',
								flightListType: this.type,
							},
							extra: data,
						},
					);
				}
			},
		);
	}

	private resetTable(): void {
		this.displayedColumns = this.getDisplayedColumns();
		if (this.type === 'upcoming') {
			this.flights = [];
			this.cursorUpcomingDate = '';
			this.allUpcomingFlightsLoaded = false;
			this.datasourceFlights = new MatTableDataSource(this.flights);
		} else if (this.type === 'historical') {
			this.flights = [];
			this.cursorHistoricalDate = '';
			this.allHistoricalFlightsLoaded = false;
			this.datasourceFlights = new MatTableDataSource(this.flights);
		} else if (this.type === 'booking') {
			this.flights = [];
			this.cursorUpcomingDate = '';
			this.allUpcomingFlightsLoaded = false;
			this.datasourceFlights = new MatTableDataSource(this.flights);
		}
	}

	updateComments(flight, event) {
		if (event) {
			this.flightUpdate = true;
			const comments = event.save ? event.comments : flight.comment;
			const tags = event.save ? event.tags : flight.tags;

			this.flightSubs.push(
				this.flightService.updateFlightComments(flight.id, comments).subscribe({
					error: (err) => this.snackBar.openSnackBarError(err.message),
				}),
			);
			if (tags) {
				this.flightSubs.push(
					this.flightService.updateFlightTags(flight.id, tags).subscribe(
						() => {},
						(err) => this.snackBar.openSnackBarError(err.message),
					),
				);
			}
		}
	}

	addToFlight(flight) {
		this.addBookingToFlight.emit(flight);
	}

	handlePassengersClick(event: Event, pass: any) {
		if (this.isXSmall) {
			event.stopPropagation();
			pass.toggle();
		}
	}

	showDialogPilotCrew(flight) {
		const dialogRef = this.dialog.open(DialogFlightPilotCrewComponent, {
			data: flight,
			disableClose: true,
			minWidth: '420px',
			hasBackdrop: true,
		});
		dialogRef.afterClosed().subscribe((result) => {
			// prepare the input object for the mutation with status CONFIRMED
			const crew: FlightCrewInput[] = [];
			const flightId = result?.id;
			if (flightId && result.pilot) {
				const pilot: FlightCrewInput = { id: result.pilot.id, isPilot: true, status: CrewStatus.Con };
				crew.push(pilot);
			}
			if (flightId && result.crew) {
				const flightId = result.id;
				result.crew.forEach((c) => {
					crew.push({ id: c.crew.id, isPilot: false, status: CrewStatus.Con });
				});
			}
			if (flightId) {
				this.flightService.saveFlightCrew(crew, flightId).subscribe((res) => {});
			}
		});
	}

	getCellStyles(xsmall, small, desktop) {
		const key = `${this.isXSmall}|${this.isSmall}|${xsmall}|${small}|${desktop}`;
		let styles = this.cellStylesCache.get(key);
		if (!styles) {
			if (this.isXSmall) {
				styles = { 'min-width': `${xsmall}px`, 'max-width': `${small}px`, padding: '0px 8px' };
			} else if (this.isSmall) {
				styles = { 'min-width': `${small}px`, 'max-width': `${desktop}px`, padding: '0px 8px' };
			} else {
				styles = { 'min-width': `${desktop}px`, 'max-width': `${desktop}px`, padding: '0px 8px' };
			}
			this.cellStylesCache.set(key, styles);
		}
		return styles;
	}
}
